import React, { useEffect, useMemo, useState } from 'react';
import { notificationApi } from '../services/api';
import type { PortalMessageAttachment } from '../utils/portalMessages';

type DeviceModelOption = {
  id: string;
  manufacturer?: string | null;
  modelName?: string | null;
  modelNumber?: string | null;
};

type Props = {
  styles: any;
  open: boolean;
  mode?: 'unit' | 'org';
  onClose: () => void;
  onCreated: () => void | Promise<void>;
  deviceModels: DeviceModelOption[];
  orgUnits?: Array<{ id: string; name?: string | null; slug?: string | null }>;
};

type NotificationFormState = {
  audience: string;
  severity: string;
  targetMode: string;
  modelId: string;
  serials: string;
  title: string;
  body: string;
  expiresAt: string;
  unitIds: string[];
};

const DEFAULT_FORM: NotificationFormState = {
  audience: 'BOTH',
  severity: 'INFO',
  targetMode: 'ALL_CUSTOMERS',
  modelId: '',
  serials: '',
  title: '',
  body: '',
  expiresAt: '',
  unitIds: []
};

function modelLabel(model: DeviceModelOption) {
  return [model.manufacturer, model.modelName, model.modelNumber].filter(Boolean).join(' - ') || model.id;
}

function orgUnitLabel(unit: { id: string; name?: string | null; slug?: string | null }) {
  return unit.name || unit.slug || unit.id;
}

function formatAttachmentSize(size: number) {
  if (!Number.isFinite(size) || size <= 0) return '0 KB';
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

export function TechDashboardNotificationModal({
  styles,
  open,
  mode = 'unit',
  onClose,
  onCreated,
  deviceModels,
  orgUnits = []
}: Props) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [attachments, setAttachments] = useState<PortalMessageAttachment[]>([]);
  const [preview, setPreview] = useState<any | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'publish' | null>(null);
  const [error, setError] = useState('');
  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
  const isOrgMode = mode === 'org';
  const isServiceOnly = form.audience === 'SERVICE';
  const orgUnitIds = useMemo(() => orgUnits.map(unit => unit.id).filter(Boolean), [orgUnits]);
  const allOrgUnitsSelected = isOrgMode && orgUnitIds.length > 0 && orgUnitIds.every(id => form.unitIds.includes(id));

  const canPreview = useMemo(() => {
    if (!form.title.trim() || !form.body.trim()) return false;
    if (isOrgMode && orgUnits.length === 0) return false;
    if (isOrgMode && form.unitIds.length === 0) return false;
    if (!isServiceOnly && form.targetMode === 'DEVICE_MODEL' && !form.modelId) return false;
    if (!isServiceOnly && form.targetMode === 'SERIALS' && !form.modelId) return false;
    if (!isServiceOnly && form.targetMode === 'SERIALS' && !form.serials.trim()) return false;
    return true;
  }, [form, isOrgMode, isServiceOnly, orgUnits.length]);

  useEffect(() => {
    if (!open || !isOrgMode) return;
    setForm(prev => {
      const retainedUnitIds = prev.unitIds.filter(id => orgUnitIds.includes(id));
      if (retainedUnitIds.length > 0) {
        return retainedUnitIds.length === prev.unitIds.length ? prev : { ...prev, unitIds: retainedUnitIds };
      }
      return { ...prev, unitIds: orgUnitIds };
    });
    setPreview(null);
  }, [isOrgMode, open, orgUnitIds]);

  if (!open) return null;

  const buildPayload = () => {
    const payload: any = {
      audience: form.audience,
      severity: form.severity,
      targetMode: isServiceOnly ? 'ALL_CUSTOMERS' : form.targetMode,
      modelId: form.modelId || undefined,
      serials: form.serials || undefined,
      title: form.title.trim(),
      body: form.body.trim(),
      expiresAt: form.expiresAt || undefined,
      attachments: attachments.length > 0 ? attachments : undefined
    };
    if (isOrgMode) {
      payload.unitIds = form.unitIds;
    }
    return payload;
  };

  const handleAttachmentChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const selected = Array.from(files).map((file, index) => ({
      id: `notification-attachment-${Date.now()}-${index}-${file.name}`,
      name: file.name,
      type: file.type || 'application/octet-stream',
      size: file.size,
      file
    }));
    setAttachments(prev => [...prev, ...selected]);
  };

  const attachmentDropStyle = {
    border: `1px dashed ${isDraggingAttachments ? '#0f4c53' : '#cdbfae'}`,
    borderRadius: '0.85rem',
    background: isDraggingAttachments ? '#eef6f3' : '#fffdf8',
    padding: '1rem',
    display: 'grid',
    gap: '0.5rem',
    cursor: busy ? 'not-allowed' : 'pointer',
    color: '#304e52'
  };

  const attachmentInputId = `notification-attachments-${mode}`;

  const handleRemoveAttachment = (attachmentId: string) => {
    setAttachments(prev => prev.filter(item => item.id !== attachmentId));
  };

  const handleClose = () => {
    setShowPreviewModal(false);
    onClose();
  };

  const handlePreview = async () => {
    if (!canPreview) return;
    setBusy('preview');
    setError('');
    try {
      const result = isOrgMode
        ? await notificationApi.previewOrgTargets(buildPayload())
        : await notificationApi.previewTargets(buildPayload());
      setPreview(result);
      setShowPreviewModal(true);
    } catch (err: any) {
      setPreview(null);
      setShowPreviewModal(false);
      setError(err?.message || 'Failed to preview notification recipients.');
    } finally {
      setBusy(null);
    }
  };

  const handlePublish = async () => {
    if (!canPreview) return;
    setBusy('publish');
    setError('');
    try {
      if (isOrgMode) {
        await notificationApi.createOrgCampaign(buildPayload());
      } else {
        await notificationApi.createCampaign(buildPayload());
      }
      setForm(DEFAULT_FORM);
      setAttachments([]);
      setPreview(null);
      await onCreated();
      setShowPreviewModal(false);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to publish notification.');
    } finally {
      setBusy(null);
    }
  };

  const modalCardStyle = {
    ...styles.modalContentWide,
    ...styles.messagesModalCard,
    maxWidth: '900px'
  };
  const modalHeaderStyle = styles.messagesModalHeader || styles.modalHeader;
  const modalTitleStyle = styles.messagesComposeTitle || styles.modalTitle;
  const modalDescriptionStyle = {
    ...(styles.messagesComposeSubtitle || styles.modalHelper),
    marginBottom: '0.25rem'
  };
  const closeButtonStyle = styles.messagesModalCloseButton || { ...styles.button, ...styles.secondaryButton };
  const actionRowStyle = {
    ...styles.modalActions,
    ...styles.messagesReplyActions
  };
  const secondaryActionStyle = {
    ...styles.button,
    ...styles.secondaryButton,
    ...styles.messagesReplyButtonSecondary
  };
  const primaryActionStyle = {
    ...styles.button,
    ...styles.primaryButton,
    ...styles.messagesReplyButton
  };
  const disabledActionStyle = {
    opacity: 0.55,
    cursor: 'not-allowed'
  };
  const selectedPreviewUnitIds = new Set(form.unitIds);
  const previewOwnerOrgId = preview?.group?.ownerOrganizationId || preview?.org?.ownerOrganizationId || preview?.ownerOrganizationId || '';
  const previewUnitRows = Array.isArray(preview?.perUnit)
    ? preview.perUnit.filter((row: any) => {
        const unitId = row?.unit?.id ? String(row.unit.id) : '';
        if (!unitId) return false;
        if (previewOwnerOrgId && unitId === String(previewOwnerOrgId)) return false;
        return orgUnitIds.includes(unitId) && selectedPreviewUnitIds.has(unitId);
      })
    : [];
  const previewWarnings = !isOrgMode && Array.isArray(preview?.warnings) ? preview.warnings : [];
  const previewRecipientCount = isOrgMode
    ? previewUnitRows.reduce((total: number, row: any) => total + Number(row?.recipientCount || 0), 0)
    : Number(preview?.recipientCount || 0);
  const previewSummary = isOrgMode
    ? `${previewRecipientCount} recipient${previewRecipientCount === 1 ? '' : 's'} across ${previewUnitRows.length} unit${
        previewUnitRows.length === 1 ? '' : 's'
      }`
    : preview?.summary;

  return (
    <div style={styles.modal}>
      <div style={modalCardStyle}>
        <div style={modalHeaderStyle}>
          <div>
            <h2 style={modalTitleStyle}>{isOrgMode ? 'Create ORG Notification' : 'Create Notification'}</h2>
            <div style={modalDescriptionStyle}>
              {isOrgMode
                ? 'Broadcast an app-opening notice to selected connected units.'
                : 'Broadcast an app-opening notice to customer portal users, service users, or both.'}
            </div>
          </div>
          <button type="button" style={closeButtonStyle} onClick={handleClose}>
            Close
          </button>
        </div>

        <div style={styles.modalForm}>
          {isOrgMode && (
            <div style={styles.modalSection}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center' }}>
                <div>
                  <div style={styles.modalItemTitle}>Active units</div>
                  <div style={styles.modalItemMeta}>
                    {allOrgUnitsSelected
                      ? 'All active units selected.'
                      : form.unitIds.length === 0
                      ? 'No active units selected.'
                      : `${form.unitIds.length} active unit${form.unitIds.length === 1 ? '' : 's'} selected.`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={secondaryActionStyle}
                    onClick={() => {
                      setForm(prev => ({ ...prev, unitIds: orgUnitIds }));
                      setPreview(null);
                    }}
                    disabled={orgUnitIds.length === 0}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    style={secondaryActionStyle}
                    onClick={() => {
                      setForm(prev => ({ ...prev, unitIds: [] }));
                      setPreview(null);
                    }}
                    disabled={orgUnitIds.length === 0}
                  >
                    Clear all
                  </button>
                </div>
              </div>
              <div style={styles.modalItemMeta}>
                Only active connected units are listed. Uncheck any unit that should not receive this ORG notice.
              </div>
              <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem', maxHeight: '220px', overflowY: 'auto' }}>
                {orgUnits.map(unit => {
                  const checked = form.unitIds.includes(unit.id);
                  return (
                    <label
                      key={unit.id}
                      style={{
                        ...styles.messagesItem,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                        cursor: 'pointer'
                      }}
                    >
                      <span style={{ display: 'grid', gap: '0.2rem' }}>
                        <span style={styles.messagesItemTitle}>{orgUnitLabel(unit)}</span>
                        <span style={styles.messagesItemMeta}>{unit.slug || unit.id}</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          setForm(prev => ({
                            ...prev,
                            unitIds: event.target.checked
                              ? Array.from(new Set([...prev.unitIds, unit.id]))
                              : prev.unitIds.filter(id => id !== unit.id)
                          }));
                          setPreview(null);
                        }}
                      />
                    </label>
                  );
                })}
              </div>
              {orgUnits.length === 0 && <div style={{ ...styles.modalHelper, color: '#a33b2f' }}>No connected units are available.</div>}
            </div>
          )}

          <div style={styles.modalRow}>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Audience</label>
              <select
                style={styles.modalSelect}
                value={form.audience}
                onChange={(event) => {
                  setForm(prev => ({ ...prev, audience: event.target.value }));
                  setPreview(null);
                }}
              >
                <option value="BOTH">Customers and service</option>
                <option value="CUSTOMERS">Customers only</option>
                <option value="SERVICE">Service only</option>
              </select>
            </div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Severity</label>
              <select
                style={styles.modalSelect}
                value={form.severity}
                onChange={(event) => setForm(prev => ({ ...prev, severity: event.target.value }))}
              >
                <option value="INFO">Info</option>
                <option value="WARNING">Warning</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Expires at</label>
              <input
                style={styles.modalInput}
                type="datetime-local"
                value={form.expiresAt}
                onChange={(event) => setForm(prev => ({ ...prev, expiresAt: event.target.value }))}
              />
            </div>
          </div>

          <div style={styles.modalRow}>
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Customer target</label>
              <select
                style={styles.modalSelect}
                value={isServiceOnly ? 'ALL_CUSTOMERS' : form.targetMode}
                disabled={isServiceOnly}
                onChange={(event) => {
                  setForm(prev => ({ ...prev, targetMode: event.target.value, modelId: '', serials: '' }));
                  setPreview(null);
                }}
              >
                <option value="ALL_CUSTOMERS">All active customers</option>
                <option value="DEVICE_MODEL">Customers with device model</option>
                <option value="SERIALS">Customers with serials/ranges</option>
              </select>
              {isServiceOnly && <div style={styles.modalHelper}>Service-only notices go to active unit staff.</div>}
            </div>
            {!isServiceOnly && (form.targetMode === 'DEVICE_MODEL' || form.targetMode === 'SERIALS') && (
              <div style={styles.modalField}>
                <label style={styles.modalLabel}>{form.targetMode === 'SERIALS' ? 'Device model required for serials' : 'Device model'}</label>
                <select
                  style={styles.modalSelect}
                  value={form.modelId}
                  onChange={(event) => {
                    setForm(prev => ({ ...prev, modelId: event.target.value }));
                    setPreview(null);
                  }}
                >
                  <option value="">Select model...</option>
                  {deviceModels.map(model => (
                    <option key={model.id} value={model.id}>
                      {modelLabel(model)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!isServiceOnly && form.targetMode === 'SERIALS' && (
            <div style={styles.modalField}>
              <label style={styles.modalLabel}>Serials or ranges</label>
              <textarea
                style={styles.modalTextarea}
                rows={4}
                value={form.serials}
                onChange={(event) => {
                  setForm(prev => ({ ...prev, serials: event.target.value }));
                  setPreview(null);
                }}
                disabled={!form.modelId}
                placeholder="Enter exact serials, comma/newline separated, or ranges like RSP0001..RSP0025"
              />
              {!form.modelId && (
                <div style={{ ...styles.modalHelper, color: '#a33b2f' }}>
                  Select the device model first. Serial targeting is model-scoped to avoid matching the same serial on another device type.
                </div>
              )}
            </div>
          )}

          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Title</label>
            <input
              style={styles.modalInput}
              value={form.title}
              onChange={(event) => {
                setForm(prev => ({ ...prev, title: event.target.value }));
                setPreview(null);
              }}
              placeholder="Recall notice, safety update, service bulletin..."
            />
          </div>

          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Message</label>
            <textarea
              style={styles.modalTextarea}
              rows={7}
              value={form.body}
              onChange={(event) => {
                setForm(prev => ({ ...prev, body: event.target.value }));
                setPreview(null);
              }}
              placeholder="Write the notification users should see when they open the app."
            />
          </div>

          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Supporting attachments</label>
            <label
              htmlFor={attachmentInputId}
              style={attachmentDropStyle}
              onDragOver={(event) => {
                event.preventDefault();
                if (!busy) setIsDraggingAttachments(true);
              }}
              onDragLeave={() => setIsDraggingAttachments(false)}
              onDrop={(event) => {
                event.preventDefault();
                setIsDraggingAttachments(false);
                if (busy) return;
                handleAttachmentChange(event.dataTransfer.files);
              }}
            >
              <strong>Drag and drop files here, or click to choose files</strong>
              <span style={styles.modalHelper}>Attach PDFs, images, or supporting notice files for recipients.</span>
              <input
                id={attachmentInputId}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => {
                  handleAttachmentChange(event.target.files);
                  event.currentTarget.value = '';
                }}
                disabled={!!busy}
              />
            </label>
            {attachments.length > 0 && (
              <div style={styles.messagesAttachmentList}>
                {attachments.map(item => (
                  <div key={item.id} style={styles.messagesAttachmentItem}>
                    <div>
                      {item.name} ({formatAttachmentSize(item.size)})
                    </div>
                    <button
                      type="button"
                      style={styles.messagesAttachmentRemove}
                      onClick={() => handleRemoveAttachment(item.id)}
                      disabled={!!busy}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div style={{ ...styles.modalHelper, color: '#a33b2f' }}>{error}</div>}

          <div style={actionRowStyle}>
            <button
              type="button"
              style={{ ...secondaryActionStyle, ...(busy ? disabledActionStyle : {}) }}
              onClick={handleClose}
              disabled={!!busy}
            >
              Cancel
            </button>
            <button
              type="button"
              style={{ ...secondaryActionStyle, ...(!canPreview || busy ? disabledActionStyle : {}) }}
              onClick={handlePreview}
              disabled={!canPreview || !!busy}
            >
              {busy === 'preview' ? 'Checking...' : 'Check recipients'}
            </button>
            <button
              type="button"
              style={{ ...primaryActionStyle, ...(!canPreview || busy ? disabledActionStyle : {}) }}
              onClick={handlePublish}
              disabled={!canPreview || !!busy}
            >
              {busy === 'publish' ? 'Publishing...' : 'Publish notification'}
            </button>
          </div>
        </div>
      </div>
      {showPreviewModal && preview && (
        <div style={styles.modal}>
          <div style={{ ...modalCardStyle, maxWidth: '720px' }}>
            <div style={modalHeaderStyle}>
              <div>
                <h2 style={modalTitleStyle}>Recipient preview</h2>
                <div style={modalDescriptionStyle}>Review the selected active unit recipients before publishing.</div>
              </div>
              <button type="button" style={closeButtonStyle} onClick={() => setShowPreviewModal(false)}>
                Close
              </button>
            </div>
            <div style={styles.modalForm}>
              <div style={styles.modalSection}>
                <div style={styles.modalItemTitle}>{previewSummary}</div>
                <div style={styles.modalItemMeta}>
                  {previewRecipientCount} recipient{previewRecipientCount === 1 ? '' : 's'} will receive this notification.
                </div>
                {isOrgMode && previewUnitRows.length > 0 && (
                  <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
                    {previewUnitRows.map((row: any) => (
                      <div key={row?.unit?.id || row?.unit?.slug} style={styles.messagesItem}>
                        <div style={styles.messagesItemTitle}>{orgUnitLabel(row.unit || {})}</div>
                        <div style={styles.messagesItemMeta}>
                          {Number(row.recipientCount || 0)} recipient{Number(row.recipientCount || 0) === 1 ? '' : 's'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {previewWarnings.map((warning: string) => (
                  <div key={warning} style={{ ...styles.modalHelper, color: '#a33b2f' }}>
                    {warning}
                  </div>
                ))}
              </div>
              <div style={actionRowStyle}>
                <button type="button" style={secondaryActionStyle} onClick={() => setShowPreviewModal(false)}>
                  Close preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
